import { Carousel } from "client/Components";
import { ReactElement } from "react";
import { Guid } from "client_legacy/ndaw/guid";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import {
	useHubItemLiveInformation,
	useHubCardSizeInformation,
	HubItemLiveInformationProvider,
	HubCardSizeInformation,
	HubCardWidth
} from "./hooks";
import {
	CardProps,
	HubTileCategoryIds
} from "./common";
import { EmptyResult, ErrorResult } from "./NoResults";
import { DimensionToRender } from "../../RenderState/common";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubItemTileResourceKind } from "client/Api/Workspace/workspace";

export type CarouselLayoutProps<TItem extends { id: number; }, TInterests> = {
	tabId: Guid;
	categoryIds: HubTileCategoryIds;
	dimension: DimensionToRender;
	provider: HubItemLiveInformationProvider<TItem, TInterests>;
	interestsFactory: (sizeInfo: HubCardSizeInformation) => TInterests;
	cardComponent: {
		(props: CardProps<TItem>): ReactElement;
	};
	onOpen: (id: number) => void;
	timezone: TimeZone;
	isAutoPlayEnabled: boolean;
};

export const CarouselLayout = <TItem extends { id: number; }, TInterests>(
	props: CarouselLayoutProps<TItem, TInterests>
): ReactElement | null => {
	const {
		tabId,
		categoryIds,
		dimension,
		provider,
		interestsFactory,
		cardComponent: CardComponent,
		onOpen,
		timezone,
		isAutoPlayEnabled
	} = props;

	const sizeInfo: HubCardSizeInformation = useHubCardSizeInformation(dimension);

	const [loadingState, refreshHandler] = useHubItemLiveInformation(
		provider,
		tabId,
		{
			kind: HubItemTileResourceKind.CategoryIds,
			categoryIds
		},
		interestsFactory(sizeInfo)
	);

	const {
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		page,
		lastPinnedItem
	} = loadingState;

	if (hasTooManyConsecutiveErrors) {
		return <TileRefresh onRefresh={refreshHandler} />;
	}

	if (hasGlobalFailure) {
		return <ErrorResult itemKind={provider.itemKind} onRefresh={refreshHandler} />;
	}

	if (!page) {
		return null;
	}

	if (page.items.length === 0) {
		return <EmptyResult itemKind={provider.itemKind} categoryIds={categoryIds} />;
	}

	let items: TItem[] = page.items;

	if (lastPinnedItem) {
		items = items
			.filter(
				x => x.id !== lastPinnedItem.id
			);

		items.unshift(lastPinnedItem);
	}

	const isSmall: boolean = sizeInfo.width === HubCardWidth.Narrow
		|| sizeInfo.width === HubCardWidth.UltraNarrow;

	return (
		<Carousel
			shouldLoopAround
			isAutoPlayEnabled={isAutoPlayEnabled}
		>
			{items.map((x) => (
				<CardComponent
					key={x.id}
					item={x}
					size={isSmall
						? "small"
						: "large"
					}
					onOpen={onOpen}
					hasTopBarBackdrop
					timezone={timezone}
				/>
			))}
		</Carousel>
	);
};
CarouselLayout.displayName = "CarouselLayout";
